// language.service.ts
import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';

@Injectable({ providedIn: 'root' })
export class LanguageService {
  readonly availableLangs = ['ca', 'en', 'es-la', 'br', 'es', 'fr', 'he', 'ar'];
  readonly storageKey = 'appLang';
  readonly rtlLangs = ['he', 'ar'];

  public langLabels: { [key: string]: string } = {
      'ca': 'Català',
      'en': 'English',
      'es-la': 'Español',
      'br': 'Português (BR)',
      'es': 'Castellano',
      'fr': 'Français',
      'he': 'עברית',
      'ar': 'العربية'
  };

  constructor(private translate: TranslateService) {
    this.translate.addLangs(this.availableLangs);
    this.translate.setDefaultLang('ca');
  }

  // Idioma a usar: la preferència guardada per l'usuari si n'hi ha una vàlida,
  // si no, sempre 'ca'. NO es consulta mai l'idioma del navegador/sistema operatiu.
  private resolveLang(): string {
    const saved = localStorage.getItem(this.storageKey);
    return (saved && this.availableLangs.includes(saved)) ? saved : 'ca';
  }

  private applyDirection(lang: string): void {
    const dir = this.rtlLangs.includes(lang) ? 'rtl' : 'ltr';
    document.documentElement.dir = dir;
    document.documentElement.lang = lang;
  }

  initAndWait(): Promise<any> {
    const lang = this.resolveLang();
    this.applyDirection(lang);
    return this.translate.use(lang).toPromise();
  }

  init(): void {
    this.setLang(this.resolveLang());
  }

  setLang(lang: string): void {
    if (!this.availableLangs.includes(lang)) { return; }
    this.translate.use(lang);
    localStorage.setItem(this.storageKey, lang);
    this.applyDirection(lang);
  }

  get currentLang(): string {
    return this.translate.currentLang;
  }

  get isRtl(): boolean {
    return this.rtlLangs.includes(this.currentLang);
  }

  label(domain: string, rawValue: string): string {
    const table = this.translate.translations?.[this.translate.currentLang]?.[domain];
    return (table && table[rawValue] !== undefined) ? table[rawValue] : rawValue;
  }
}